import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Download,
  KeyRound,
  Loader2,
  RefreshCw,
  Shield,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OtpInput } from "@/components/auth/OtpInput";
import {
  disable2FA,
  getMe,
  regenerateBackupCodes,
  setup2FA,
  verifySetup2FA,
} from "@/api/auth";
import type { Setup2FAResponse } from "@/api/types";
import { useAuthStore } from "@/stores/auth.store";
import { cn } from "@/lib/utils";

type SetupStep = "scan" | "verify" | "codes";

export function TwoFactorSection() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const enabled = user?.totp_enabled === true;

  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  async function refreshMe() {
    try {
      const me = await getMe();
      setUser(me);
    } catch {
      // Best-effort refresh — surfaces won't auto-update if it fails, but it's not critical.
    }
  }

  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-lg font-medium tracking-tight text-slate-900">
          Security
        </h2>
        <p className="text-sm text-slate-500">
          Manage your account security and sign-in protection
        </p>
      </header>

      <div className="rounded-plane bg-white p-6 shadow-plane">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {enabled ? (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                <ShieldCheck className="h-5 w-5" />
              </div>
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <Shield className="h-5 w-5" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-slate-900">
                  Two-factor authentication
                </h3>
                {enabled ? (
                  <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                    Enabled
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                    Disabled
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {enabled
                  ? "Your account is protected with two-factor authentication"
                  : "Add an extra layer of security to your account"}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {enabled ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRegenOpen(true)}
                className="border-amber-200 text-amber-800 hover:bg-amber-50"
              >
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Regenerate backup codes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDisableOpen(true)}
                className="border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <ShieldOff className="mr-2 h-3.5 w-3.5" />
                Disable 2FA
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setSetupOpen(true)}
            >
              <ShieldCheck className="mr-2 h-3.5 w-3.5" />
              Enable 2FA
            </Button>
          )}
        </div>
      </div>

      <SetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        onCompleted={refreshMe}
      />

      <DisableDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        onCompleted={refreshMe}
      />

      <RegenerateDialog open={regenOpen} onOpenChange={setRegenOpen} />
    </section>
  );
}

// ─── Setup dialog ─────────────────────────────────────────────────────────

interface SetupDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCompleted: () => Promise<void>;
}

function SetupDialog({ open, onOpenChange, onCompleted }: SetupDialogProps) {
  const [step, setStep] = useState<SetupStep>("scan");
  const [data, setData] = useState<Setup2FAResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setStep("scan");
      setData(null);
      setCode("");
      setBackupCodes([]);
      return;
    }
    let cancelled = false;
    async function init() {
      setLoading(true);
      try {
        const res = await setup2FA();
        if (!cancelled) setData(res);
      } catch (err) {
        toast.error(extractMessage(err));
        if (!cancelled) onOpenChange(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, [open, onOpenChange]);

  async function handleVerify() {
    if (code.length !== 6) return;
    setVerifying(true);
    try {
      const res = await verifySetup2FA(code);
      // Refresh the auth store *before* transitioning so the parent section
      // already shows "Enabled" by the time the dialog closes.
      await onCompleted();
      setBackupCodes(res.backup_codes);
      setStep("codes");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        toast.error("Invalid code");
      } else {
        toast.error(extractMessage(err));
      }
      setCode("");
    } finally {
      setVerifying(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (verifying) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-[520px]">
        {step === "scan" && (
          <>
            <DialogHeader>
              <DialogTitle>Set up two-factor authentication</DialogTitle>
              <DialogDescription>
                Scan this QR code with your authenticator app (Google
                Authenticator, Authy, 1Password)
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col items-center py-2">
              {loading || !data ? (
                <div className="flex h-[200px] w-[200px] items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <img
                  src={qrCodeSrc(data.qr_code_base64)}
                  alt="2FA QR code"
                  width={200}
                  height={200}
                  className="rounded-lg border border-slate-200"
                />
              )}

              {data && (
                <div className="mt-5 w-full">
                  <p className="text-center text-xs text-slate-500">
                    Can&rsquo;t scan? Enter this code manually:
                  </p>
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <code className="flex-1 select-all break-all text-center font-mono text-sm tracking-wide text-slate-900">
                      {formatSecret(data.secret)}
                    </code>
                    <CopyButton value={data.secret} label="Copy" />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => setStep("verify")}
                disabled={loading || !data}
              >
                Next
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "verify" && (
          <>
            <DialogHeader>
              <DialogTitle>Verify your authenticator</DialogTitle>
              <DialogDescription>
                Enter the 6-digit code from your authenticator app to confirm
                setup
              </DialogDescription>
            </DialogHeader>

            <div className="py-2">
              <OtpInput
                value={code}
                onChange={setCode}
                autoFocus
                disabled={verifying}
                onComplete={() => void handleVerify()}
              />
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => {
                  setCode("");
                  setStep("scan");
                }}
                disabled={verifying}
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleVerify()}
                disabled={code.length !== 6 || verifying}
              >
                {verifying ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  "Verify & Enable"
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "codes" && (
          <BackupCodesPanel
            title="Two-factor authentication enabled!"
            codes={backupCodes}
            onClose={() => onOpenChange(false)}
            confirmLabel="I've saved my codes"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Disable dialog ───────────────────────────────────────────────────────

interface DisableDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCompleted: () => Promise<void>;
}

function DisableDialog({
  open,
  onOpenChange,
  onCompleted,
}: DisableDialogProps) {
  const [step, setStep] = useState<"code" | "confirm">("code");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep("code");
      setCode("");
    }
  }, [open]);

  async function handleDisable() {
    setSubmitting(true);
    try {
      await disable2FA(code);
      // Refresh the auth store before closing so the parent flips to
      // "Disabled" without a flash of the old "Enabled" state.
      await onCompleted();
      toast.success("Two-factor authentication disabled");
      onOpenChange(false);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        toast.error("Invalid code");
        setCode("");
        setStep("code");
      } else {
        toast.error(extractMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (submitting) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-[460px]">
        {step === "code" ? (
          <>
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                <ShieldOff className="h-5 w-5" />
              </div>
              <DialogTitle className="text-center">Disable 2FA</DialogTitle>
              <DialogDescription className="text-center">
                Enter the 6-digit code from your authenticator app to confirm
              </DialogDescription>
            </DialogHeader>

            <div className="py-2">
              <OtpInput
                value={code}
                onChange={setCode}
                autoFocus
                disabled={submitting}
                onComplete={() => setStep("confirm")}
              />
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => setStep("confirm")}
                disabled={code.length !== 6}
              >
                Next
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Are you sure?</DialogTitle>
              <DialogDescription>
                Your account will be less secure without two-factor
                authentication.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setStep("code")}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDisable()}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Disabling…
                  </>
                ) : (
                  "Disable 2FA"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Regenerate backup codes ─────────────────────────────────────────────

interface RegenerateDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

function RegenerateDialog({ open, onOpenChange }: RegenerateDialogProps) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);

  useEffect(() => {
    if (!open) {
      setCode("");
      setCodes(null);
    }
  }, [open]);

  async function handleRegen() {
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      const res = await regenerateBackupCodes(code);
      setCodes(res.backup_codes);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        toast.error("Invalid code");
        setCode("");
      } else {
        toast.error(extractMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (submitting) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-[520px]">
        {codes ? (
          <BackupCodesPanel
            title="New backup codes generated"
            codes={codes}
            onClose={() => onOpenChange(false)}
            confirmLabel="I've saved my new codes"
          />
        ) : (
          <>
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <KeyRound className="h-5 w-5" />
              </div>
              <DialogTitle className="text-center">
                Regenerate backup codes
              </DialogTitle>
              <DialogDescription className="text-center">
                Enter your current 6-digit code. Your old backup codes will
                stop working immediately.
              </DialogDescription>
            </DialogHeader>

            <div className="py-2">
              <OtpInput
                value={code}
                onChange={setCode}
                autoFocus
                disabled={submitting}
                onComplete={() => void handleRegen()}
              />
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleRegen()}
                disabled={code.length !== 6 || submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Generating…
                  </>
                ) : (
                  "Generate new codes"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Backup codes panel (shared) ─────────────────────────────────────────

interface BackupCodesPanelProps {
  title: string;
  codes: string[];
  onClose: () => void;
  confirmLabel: string;
}

function BackupCodesPanel({
  title,
  codes,
  onClose,
  confirmLabel,
}: BackupCodesPanelProps) {
  async function copyAll() {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("All codes copied");
    } catch {
      toast.error("Couldn't copy. Select manually instead.");
    }
  }

  function downloadFile() {
    const blob = new Blob(
      [
        "Prism backup codes\n",
        "Each code can be used once. Keep these somewhere safe.\n\n",
        codes.join("\n"),
        "\n",
      ],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "prism-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <DialogHeader>
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <DialogTitle className="text-center text-[18px]">{title}</DialogTitle>
        <DialogDescription className="text-center">
          Save these backup codes in a safe place. Each code can only be used
          once.
        </DialogDescription>
      </DialogHeader>

      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-5">
        <div className="grid grid-cols-2 gap-2">
          {codes.map((c) => (
            <code
              key={c}
              className="select-all rounded-md border border-amber-200/60 bg-white px-3 py-2 text-center font-mono text-base tracking-wide text-slate-900"
            >
              {c}
            </code>
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void copyAll()}
            className="flex-1 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
          >
            <Copy className="mr-2 h-3.5 w-3.5" />
            Copy all codes
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadFile}
            className="flex-1 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
          >
            <Download className="mr-2 h-3.5 w-3.5" />
            Download as text file
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200/60 bg-amber-50/30 px-3 py-2.5 text-xs text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          These codes will not be shown again. If you lose your authenticator
          and your backup codes, you will be locked out of your account.
        </p>
      </div>

      <DialogFooter>
        <Button variant="primary" onClick={onClose}>
          {confirmLabel}
        </Button>
      </DialogFooter>
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => void copy()}
      className={cn(
        "h-8 shrink-0 px-2.5 text-xs",
        copied && "border-emerald-200 text-emerald-700",
      )}
    >
      <Copy className="mr-1.5 h-3 w-3" />
      {copied ? "Copied" : label}
    </Button>
  );
}

function formatSecret(secret: string): string {
  return secret.replace(/\s+/g, "").match(/.{1,4}/g)?.join(" ") ?? secret;
}

/**
 * Backend may return either a full data URI or just the raw base64 — handle
 * both so the <img> renders regardless of which shape lands.
 */
function qrCodeSrc(value: string): string {
  return value.startsWith("data:") ? value : `data:image/png;base64,${value}`;
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
